import { useMemo, useState } from 'react';
import clsx from 'clsx';
import { Check, ChevronRight, ExternalLink, X } from 'lucide-react';
import type { TaskPatch, TaskSuggestion } from '../../api/organizer';
import { Button } from '../../components/ui/Button';
import { EmptyState, ErrorState, LoadingState } from '../../components/ui/States';
import { Pagination, usePagination } from '../../components/ui/Pagination';
import { useAcceptSuggestion, useFathomIngests, useRejectSuggestion, useSuggestions } from '../../hooks/useOrganizer';
import { formatDayMonthTime, formatTime } from '../../utils/dateFormat';
import { callCountLabel, dueLabel } from './taskUtils';
import { SuggestionCard } from './SuggestionCard';
import { FathomSyncPanel } from './FathomSyncPanel';

/** Calls per page. A call's to-dos are never split across pages. */
const CALLS_PER_PAGE = 10;

/** Suggestions older than this fold under "Older (N)". Display only: nothing is removed. */
const OLDER_AFTER_MS = 14 * 86_400_000;

/** Today as YYYY-MM-DD in local time, to compare against date-only due dates. */
function todayIso(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** When the suggestion's call happened, else when it was proposed. */
function suggestionAt(s: TaskSuggestion): number {
  return new Date(s.evidence?.recordedAt ?? s.createdAt).getTime();
}

interface CallGroup {
  key: string;
  title: string;
  recordedAt: string | null;
  shareUrl: string | null;
  items: TaskSuggestion[];
}

/** One group per call (Fathom recording), newest call first. */
function groupByCall(suggestions: TaskSuggestion[]): CallGroup[] {
  const groups = new Map<string, CallGroup>();
  for (const s of suggestions) {
    const key = `${s.source}:${s.sourceRef}`;
    const existing = groups.get(key);
    if (existing) existing.items.push(s);
    else
      groups.set(key, {
        key,
        title: s.evidence?.meetingTitle ?? 'Untitled call',
        recordedAt: s.evidence?.recordedAt ?? null,
        shareUrl: s.evidence?.shareUrl ?? null,
        items: [s],
      });
  }
  return [...groups.values()].sort((a, b) => (b.recordedAt ?? '').localeCompare(a.recordedAt ?? ''));
}

function confidenceShort(confidence: string | null): string | null {
  if (confidence === null) return null;
  const value = Number(confidence);
  if (Number.isNaN(value)) return null;
  return value >= 0.8 ? 'High' : value >= 0.5 ? 'Medium' : 'Low';
}

/**
 * The most common confidence level, when one level covers more than half of the
 * suggestions. Without a clear majority every row keeps its own label.
 */
function majorityConfidence(suggestions: TaskSuggestion[]): { level: string; count: number } | null {
  const counts = new Map<string, number>();
  for (const s of suggestions) {
    const level = confidenceShort(s.confidence);
    if (level) counts.set(level, (counts.get(level) ?? 0) + 1);
  }
  let best: { level: string; count: number } | null = null;
  for (const [level, count] of counts) if (!best || count > best.count) best = { level, count };
  return best && best.count * 2 > suggestions.length ? best : null;
}

/**
 * The review queue for to-dos extracted from calls.
 *
 * Nothing here is on the board yet, and nothing gets there without a click — a
 * bulk accept is still an explicit click on a visible selection. Failures stay
 * on the row they belong to; nothing is accepted or dropped silently.
 */
export function SuggestionsInbox() {
  const { data, isLoading, isError, error, refetch, dataUpdatedAt } = useSuggestions();
  const accept = useAcceptSuggestion();
  const reject = useRejectSuggestion();

  const [failures, setFailures] = useState<Record<string, string>>({});
  const [pendingIds, setPendingIds] = useState<Set<string>>(new Set());
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [bulkRunning, setBulkRunning] = useState(false);
  const [showOlder, setShowOlder] = useState(false);

  const suggestions = useMemo(() => data?.suggestions ?? [], [data]);
  const { recent, older } = useMemo(() => {
    const cutoff = Date.now() - OLDER_AFTER_MS;
    return {
      recent: suggestions.filter((s) => suggestionAt(s) >= cutoff),
      older: suggestions.filter((s) => suggestionAt(s) < cutoff),
    };
  }, [suggestions]);
  // Group first, then page by call: paging by suggestion split a call across
  // pages, so its header said "3" while the call had 4 waiting.
  const recentGroups = useMemo(() => groupByCall(recent), [recent]);
  const olderAllGroups = useMemo(() => groupByCall(older), [older]);
  const { pageItems: groups, page, pageCount, setPage, total, pageSize } = usePagination(recentGroups, CALLS_PER_PAGE);
  const olderPager = usePagination(olderAllGroups, CALLS_PER_PAGE);
  const olderGroups = olderPager.pageItems;
  const today = todayIso();

  // How many to-dos each call produced, from the same ingest list "Calls
  // checked" reads, so both places say the same for one call ("4 waiting", or "4 found · 3 waiting").
  const ingests = useFathomIngests();
  const foundByCall = useMemo(() => {
    const map = new Map<string, number>();
    for (const i of ingests.data?.ingests ?? []) {
      if (i.state === 'extracted') map.set(`fathom:${i.fathomRecordingId}`, i.suggestionsCreated);
    }
    return map;
  }, [ingests.data]);

  function setPending(id: string, on: boolean) {
    setPendingIds((prev) => {
      const next = new Set(prev);
      if (on) next.add(id);
      else next.delete(id);
      return next;
    });
  }

  function recordFailure(id: string, err: unknown) {
    setFailures((prev) => ({ ...prev, [id]: err instanceof Error ? err.message : String(err) }));
  }

  function clearFailure(id: string) {
    setFailures((prev) => {
      if (!(id in prev)) return prev;
      const next = { ...prev };
      delete next[id];
      return next;
    });
  }

  async function runOne(kind: 'accept' | 'reject', id: string, edits?: TaskPatch) {
    clearFailure(id);
    setPending(id, true);
    try {
      // The card comes back via the hook's rollback on failure; the reason is
      // kept on the row, or a suggestion reappearing looks like a UI glitch.
      if (kind === 'accept') await accept.mutateAsync({ id, edits });
      else await reject.mutateAsync({ id });
      setSelected((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    } catch (err) {
      recordFailure(id, err);
    } finally {
      setPending(id, false);
    }
  }

  /** One at a time, so each optimistic update and rollback stays consistent. */
  async function runBulk(kind: 'accept' | 'reject') {
    setBulkRunning(true);
    try {
      for (const id of [...selected]) await runOne(kind, id);
    } finally {
      setBulkRunning(false);
    }
  }

  function toggle(ids: string[], on: boolean) {
    setSelected((prev) => {
      const next = new Set(prev);
      for (const id of ids) {
        if (on) next.add(id);
        else next.delete(id);
      }
      return next;
    });
  }

  if (isLoading) return <LoadingState rows={5} />;

  if (isError) {
    return <ErrorState title="Couldn’t load your suggestions" error={error} onRetry={() => void refetch()} />;
  }

  if (suggestions.length === 0) {
    return (
      <div className="space-y-4">
        <EmptyState
          title="Nothing to review"
          description={
            <>
              To-dos picked up from your Fathom calls land here first.
              {/* An empty inbox and a broken pipeline look identical without this. */}
              {dataUpdatedAt > 0 && ` Last checked ${formatTime(new Date(dataUpdatedAt))}.`}
            </>
          }
        />
        <FathomSyncPanel />
      </div>
    );
  }

  const failedCount = Object.keys(failures).length;
  // The majority confidence is said once in the header; a row shows its own
  // confidence only when it differs, so "High" isn't repeated on every row.
  const majority = majorityConfidence(suggestions);

  const renderGroups = (list: CallGroup[]) =>
    list.map((group) => {
          const ids = group.items.map((s) => s.id);
          const allSelected = ids.every((id) => selected.has(id));
          return (
            <div key={group.key} className="rounded-2xl border border-border-light bg-surface-primary">
              <div className="flex items-center gap-3 border-b border-border-light px-4 py-2">
                <input
                  type="checkbox"
                  aria-label={`Select all from ${group.title}`}
                  checked={allSelected}
                  onChange={(e) => toggle(ids, e.target.checked)}
                />
                <span className="min-w-0 flex-1 truncate text-body-sm font-medium text-text-primary">{group.title}</span>
                {group.recordedAt && (
                  <span className="text-body-xs text-text-tertiary">{formatDayMonthTime(group.recordedAt)}</span>
                )}
                <span className="text-body-xs text-text-muted">
                  {foundByCall.has(group.key)
                    ? callCountLabel(foundByCall.get(group.key)!, group.items.length)
                    : `${group.items.length} waiting`}
                </span>
                {group.shareUrl && (
                  <a
                    href={group.shareUrl}
                    target="_blank"
                    rel="noreferrer"
                    title="Open the call"
                    aria-label="Open the call"
                    className="text-text-tertiary hover:text-text-primary"
                  >
                    <ExternalLink className="h-3.5 w-3.5" />
                  </a>
                )}
              </div>

              <ul className="divide-y divide-border-light">
                {group.items.map((s) => {
                  const pending = pendingIds.has(s.id);
                  const expanded = expandedId === s.id;
                  const ownConfidence = confidenceShort(s.confidence);
                  const confidence = ownConfidence === majority?.level ? null : ownConfidence;
                  return (
                    <li key={s.id} className={clsx(pending && 'opacity-50')}>
                      <div className="flex items-center gap-3 px-4 py-2">
                        <input
                          type="checkbox"
                          aria-label={`Select ${s.proposedTitle}`}
                          checked={selected.has(s.id)}
                          onChange={(e) => toggle([s.id], e.target.checked)}
                        />
                        <button
                          type="button"
                          onClick={() => setExpandedId(expanded ? null : s.id)}
                          aria-expanded={expanded}
                          className="flex min-w-0 flex-1 items-center gap-1.5 text-left"
                        >
                          <ChevronRight
                            className={clsx('h-3.5 w-3.5 flex-none text-text-muted transition-transform', expanded && 'rotate-90')}
                          />
                          <span className="truncate text-body-sm text-text-primary">{s.proposedTitle}</span>
                        </button>
                        {s.proposedDueDate && (
                          <span
                            className={clsx(
                              'text-body-xs',
                              s.proposedDueDate.slice(0, 10) < today ? 'font-medium text-error' : 'text-text-tertiary',
                            )}
                            title={`Due ${s.proposedDueDate.slice(0, 10)}`}
                          >
                            {dueLabel(s.proposedDueDate.slice(0, 10), today)}
                          </span>
                        )}
                        {confidence && (
                          <span className="hidden text-body-xs text-text-muted sm:inline" title="Confidence">
                            {confidence} confidence
                          </span>
                        )}
                        <button
                          type="button"
                          disabled={pending || bulkRunning}
                          onClick={() => void runOne('accept', s.id)}
                          title="Accept"
                          aria-label={`Accept ${s.proposedTitle}`}
                          className="flex h-7 w-7 items-center justify-center rounded-full text-text-tertiary hover:bg-success-light hover:text-success-dark disabled:opacity-40"
                        >
                          <Check className="h-4 w-4" />
                        </button>
                        <button
                          type="button"
                          disabled={pending || bulkRunning}
                          onClick={() => void runOne('reject', s.id)}
                          title="Reject"
                          aria-label={`Reject ${s.proposedTitle}`}
                          className="flex h-7 w-7 items-center justify-center rounded-full text-text-tertiary hover:bg-surface-tertiary hover:text-text-primary disabled:opacity-40"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                      {failures[s.id] && !expanded && (
                        <p className="px-4 pb-2 pl-11 text-body-xs text-error" role="alert">
                          {failures[s.id]}
                        </p>
                      )}
                      {expanded && (
                        <div className="px-4 pb-3 pl-11">
                          <SuggestionCard
                            suggestion={s}
                            pending={pending}
                            error={failures[s.id]}
                            onAccept={(edits) => void runOne('accept', s.id, edits)}
                            onReject={() => void runOne('reject', s.id)}
                          />
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
          );
        });

  return (
    <div className="space-y-4">
      <div className="flex min-h-9 flex-wrap items-center justify-between gap-3">
        <p className="text-body-xs text-text-tertiary">
          {suggestions.length} to review, grouped by call
          {older.length > 0 && ` · ${older.length} older than 14 days folded below`}
          {majority &&
            ` · ${majority.count === suggestions.length ? 'all' : 'mostly'} ${majority.level.toLowerCase()} confidence`}
          {failedCount > 0 && <span className="text-error"> · {failedCount} failed, see the rows below</span>}
        </p>
        {selected.size > 0 && (
          <div className="flex items-center gap-2">
            <span className="text-body-xs text-text-secondary">{selected.size} selected</span>
            <Button size="sm" onClick={() => void runBulk('accept')} isLoading={bulkRunning} disabled={bulkRunning}>
              Accept
            </Button>
            <Button size="sm" variant="outline" onClick={() => void runBulk('reject')} disabled={bulkRunning}>
              Reject
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setSelected(new Set())} disabled={bulkRunning}>
              Clear
            </Button>
          </div>
        )}
      </div>

      {recent.length > 0 && <div className="space-y-3">{renderGroups(groups)}</div>}
      {recent.length === 0 && older.length > 0 && (
        <p className="text-body-xs text-text-tertiary">Nothing from the last 14 days.</p>
      )}
      {recent.length > 0 && (
        <Pagination page={page} pageCount={pageCount} total={total} pageSize={pageSize} onPage={setPage} />
      )}

      {older.length > 0 && (
        <div className="space-y-3">
          <button
            type="button"
            onClick={() => setShowOlder((v) => !v)}
            aria-expanded={showOlder}
            className="flex items-center gap-1 text-body-xs font-medium text-text-secondary hover:text-text-primary"
          >
            <ChevronRight className={clsx('h-3.5 w-3.5 transition-transform', showOlder && 'rotate-90')} aria-hidden />
            Older ({older.length})
            <span className="font-normal text-text-tertiary">· calls more than 14 days ago, still waiting</span>
          </button>
          {showOlder && (
            <>
              <div className="space-y-3">{renderGroups(olderGroups)}</div>
              <Pagination
                page={olderPager.page}
                pageCount={olderPager.pageCount}
                total={olderPager.total}
                pageSize={olderPager.pageSize}
                onPage={olderPager.setPage}
              />
            </>
          )}
        </div>
      )}
      <FathomSyncPanel />
    </div>
  );
}
